import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import type { ModalContextType } from "@/components/core/modals/types";
import { useModal } from "@/components/core/modals/use-modal.ts";
import { FileViewer } from "@/components/document-viewer/file-viewer.tsx";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import type { Media } from "@/features/dashboard/types/media.ts";
import { downloadFile } from "@/utils/download-file";
import { formatDate } from "@/utils/format-date.ts";
import { formatFileSize } from "@/utils/format-file-size";
import { IconDatabaseExclamation, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import CaseFileUploadModal from "../case-classification/case-file-upload-modal";
import { useCaseRecordMediaDeleteMutation, useCaseRecordPaymentProofMutation } from "../case-record/api/queries/case-records-query";
import type { CaseRecord } from "../case-record/types/case-records";

interface FileUploadButtonProps {
	modal: ModalContextType;
	data: CaseRecord;
	setFiles: React.Dispatch<React.SetStateAction<File[]>>;
	files: File[];
}

function FileUploadButton({ modal, data }: FileUploadButtonProps) {
	const [files, setFiles] = useState<File[]>([]);
	const attachmentsMutation = useCaseRecordPaymentProofMutation();

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(event.target.files || []);
		if (selectedFiles.length > 0) {
			const updatedFiles = [...files, ...selectedFiles];
			setFiles(updatedFiles);

			const mediaModal = modal.openModal({
				title: "Upload Case Complaint Documents",
				size: "lg",
				showHeader: true,
				showFooter: false,
				component: (
					<CaseFileUploadModal
						onSubmit={async (value) => {
							await attachmentsMutation.mutateAsync({
								id: data.id,
								files: value,
							});
							setFiles([]);
							modal.closeModal(mediaModal);
						}}
						initialFiles={updatedFiles}
					/>
				),
			});
		}
	};

	return (
		<Button className="relative" type="button" size="sm">
			<input type="file" multiple className="absolute opacity-0 inset-0" onChange={handleFileChange} />
			<IconPlus size={14} stroke={2} />
			<span className="">Add File</span>
		</Button>
	);
}

export default function CaseAttachmentsTable({
	title = "media Receipts",
	data,
	allowAdd = false,
	allowRemove = true,
	allowPreview = true,
	allowPrint = false,
	allowDownload = false,
}: {
	title: string;
	data: CaseRecord;
	counts: number;
	allowRemove: boolean;
	allowAdd: boolean;
	allowPreview: boolean;
	allowPrint: boolean;
	allowDownload: boolean;
}) {
	const modal = useModal();
	const documentTokenMutation = useDocumentConfigMutation();
	const attachmentDeleteMutation = useCaseRecordMediaDeleteMutation();

	const handleDownload = (attachment: Media) => {
		downloadFile(attachment.original_url, attachment.file_name);
	};

	const handleClick = async (attachment: Media) => {
		const fileExtension = attachment.file_name.split(".").pop();

		let response = null;

		if (!["image/jpeg", "image/png", "png", "jpeg", "video/mp4", "mp4"].includes(fileExtension)) {
			response = await documentTokenMutation.mutateAsync({
				file_type: fileExtension,
				title: attachment.name,
				url: attachment.original_url,
				mode: "view",
			});
		}

		modal.openModal({
			title: "Default Modal",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} token={response ? response?.token : null} options={response ? response?.options : null} />,
		});
	};
	const [files, setFiles] = useState<File[]>([]);

	const handleRemove = async (caseRecordId: string, mediaId: string) => {
		await attachmentDeleteMutation.mutateAsync({ caseRecordId, mediaId });
		modal.closeModal(m);
		// const m = modal.openModal({
		//   type: "confirm",
		//   title: "Confirm Archive",
		//   variant: "red",
		//   description: "Are you sure you want to remove this media?",
		//   onConfirm: async () => {
		//     await attachmentDeleteMutation.mutateAsync({ caseRecordId, mediaId });
		//     modal.closeModal(m);
		//   },
		// });
	};

	const caseDocumentsColumns: ColumnDef<FileRecord>[] = [
		{
			id: "name",
			label: "File Name",
			cellClassName: "",
			cell: ({ data }) => (
				<div className="flex items-center">
					<button
						type="button"
						onClick={() => handleClick(data)}
						className={
							data.custom_properties?.is_amend === true
								? "font-semibold text-red-600 hover:text-blue-500 line-through"
								: "font-semibold text-blue-600 hover:text-blue-500"
						}
					>
						{data.name}
					</button>
				</div>
			),
		},
		{
			id: "mime_type",
			label: "File Type",
			cellClassName: "",
			cell: ({ data }) => data.mime_type,
		},
		{
			id: "size",
			label: "File Size",
			cellClassName: "",
			cell: ({ data }) => <span>{formatFileSize(data.size)}</span>,
		},
		{
			id: "date_created",
			label: "Date Created",
			cellClassName: "text-right",
			headerClassName: "text-right",
			cell: ({ data }) => <span>{formatDate(data.created_at, "PPp")}</span>,
		},
	];
	return (
		<div className="flex flex-col w-full rounded-md overflow-hidden">
			<div className="flex flex-row items-center justify-between w-full p-4 gap-4 bg-slate-50">
				<span className="text-base font-semibold">{title}</span>
				{allowAdd && <FileUploadButton modal={modal} data={data} files={files} setFiles={setFiles} />}
			</div>

			{data?.medias?.length !== 0 ? (
				<div className="flex flex-col w-full gap-2 pb-4">
					<DataTable
						className="border-collapse"
						data={data?.medias}
						columns={caseDocumentsColumns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
						onDelete={(media) => allowRemove && handleRemove(data.id!, media.id)}
					/>

					{/* {data?.medias?.map((media: Media, index) => (
            <div key={index} className="flex flex-col w-full px-4 py-1 gap-2">
              <div className="flex flex-col w-full gap-2">
                <div className="flex flex-row w-full gap-2">
                  <div className="inline-flex justify-center items-center w-[50px] h-[50px] rounded-md bg-red-500 text-white">
                    <IconFileTypePdf size={24} stroke={1.5} />
                  </div>
                  <div className="inline-flex flex-col w-auto">
                    <button type="button" onClick={() => handleClick(media)} className="text-left text-base font-semibold text-blue-600 hover:text-blue-500">
                      {media.file_name}
                    </button>
                    <span className="text-sm text-foreground/70">Uploaded on {formatDate(media.created_at)}</span>
                    <span className="text-sm text-foreground/70">{formatNumber(media.size, "0,0b")}</span>
                  </div>
                  <div className="inline-flex w-auto gap-3 ml-auto">
                    <div className="inline-flex flex-row w-auto gap-1">
                      {allowDownload && (
                        <Tooltip content="Download" side="top">
                          <Button size="icon" variant="ghost" onClick={() => handleDownload(media)}>
                            <IconDownload size={16} stroke={2} />
                          </Button>
                        </Tooltip>
                      )}
                      {allowPreview && (
                        <Tooltip content="Preview" side="top">
                          <Button size="icon" variant="ghost" onClick={() => handleClick(media)}>
                            <IconEye size={16} stroke={2} />
                          </Button>
                        </Tooltip>
                      )}
                      {allowPrint && (
                        <Tooltip content="Print" side="top">
                          <Button size="icon" variant="ghost">
                            <IconPrinter size={16} stroke={2} />
                          </Button>
                        </Tooltip>
                      )}
                      {allowRemove && (
                        <Tooltip content="Remove" side="top">
                          <Button size="icon" variant="danger" type="button" onClick={() => handleRemove(data.id!, media.id)}>
                            <IconTrash size={16} stroke={2} />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))} */}
				</div>
			) : (
				<Blanket width="sm">
					<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
					<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
				</Blanket>
			)}
		</div>
	);
}
